<script setup>
import { useRoute } from 'vue-router';
import {computed, inject, onBeforeMount, ref, watch} from "vue";
import {useAuthUserStore} from "../Store/AuthUserStore.js";
import {useSubscriptionStore} from "../Store/SubscriptionStore.js";
import {useToast} from "vue-toast-notification";
import {MODULES} from "../Constants/modules.js";

const $toast = useToast();
const route = useRoute();
const brandConfig = inject('brandConfig');
const authUserStore = useAuthUserStore();
const subscriptionStore = useSubscriptionStore();
const authUser = computed(() => authUserStore.authUser);
const isSubscribed = ref(false);

// Subscription route helper
const subscriptionRoute = computed(() => {
    return subscriptionStore.isSubscribed
        ? { name: 'subscription.manage' }
        : { name: 'subscription.paywall' }
});

const subscriptionLabel = computed(() => {
    return subscriptionStore.isSubscribed ? 'Mon abonnement' : 'S\'abonner'
});

// Module access helper (visual only - router handles actual access control)
const hasModuleAccess = (moduleSlug) => {
    return subscriptionStore.hasModuleAccess(moduleSlug);
};

onBeforeMount(async () => {
    getNotifications();
    // Load accessible modules for subscription/billing checks
    await subscriptionStore.fetchAccessibleModules();
    await subscriptionStore.fetchSubscription();
});

const hideSidebarOnClickOnMobile = () => {
    const windowSize = document.documentElement.clientWidth;
    if (windowSize <= 767 && document.body.classList.contains("vertical-sidebar-enable")) {
        document.body.classList.remove("vertical-sidebar-enable");
    }
};

const isActiveMenuParent = (parent) => {
    return parent === route.meta.menu.parent;
};

const isActiveMenuChild = (child) => {
    return child === route.meta.menu.child;
};

const notifications = ref({});
function getNotifications() {
    axios.get('/api/notifications')
        .then(response => {
            notifications.value = response.data?.data ?? {};
        })
        .catch(errors => {
            console.log(errors);
        })
}

function subscribeToCentersNotificationsChannel() {
    if (authUser.value && Array.isArray(authUser.value.centers)) {
        authUser.value.centers.forEach(center => {
            Echo.channel('notifications_center_' + center.id)
                .listen('CenterNotificationNumberUpdatedEvent', (e) => {
                    getNotifications();
                });
        });
    }
}

function subscribeToUserNotificationsChannel() {
    Echo.channel('notifications_user_' + authUser.value.id)
        .listen('UserNotificationNumberUpdatedEvent', (e) => {
            getNotifications();
        });
}

function subscribeToBackOfficeNotificationsChannel() {
    Echo.channel('notifications_bo')
        .listen('BackOfficeNotificationNumberUpdatedEvent', (e) => {
            getNotifications();
        });
}

function subscribeToEveryoneNotificationsChannel() {
    Echo.channel('notifications_everyone')
        .listen('EveryoneNotificationUpdatedEvent', (e) => {
            getNotifications();
        })
        .listen('NewAppVersionEvent', (e) => {
            $toast.warning('Nouvelle version de l\'application disponible. Veuillez cliquer ici ou vous déconnecter/reconnecter', {
                duration: 0,
                position: 'top',
                dismissible: true,
                pauseOnHover: true,
                closeOnClick: false,
                onDismiss: () => {
                    location.reload();
                }
            });
        });
}



watch(authUser, (newAuthUser) => {
    if (newAuthUser && !isSubscribed.value) {
        subscribeToCentersNotificationsChannel();
        subscribeToUserNotificationsChannel();
        subscribeToEveryoneNotificationsChannel();
        if (newAuthUser.role === 'admin' || newAuthUser.role === 'back_office') {
            subscribeToBackOfficeNotificationsChannel();
        }
        isSubscribed.value = true;
    }
});

</script>

<template>
    <ul class="navbar-nav" id="navbar-nav" data-simplebar>
        <!-- GÉNÉRAL -->
        <li class="menu-title"><span data-key="t-menu">Général</span></li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'dashboard' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('dashboard'), 'text-muted': !hasModuleAccess(MODULES.DASHBOARD) }"
            >
                <i class="mdi mdi-view-grid-plus-outline"></i> <span data-key="t-starter">Dashboard</span>
                <i v-if="!hasModuleAccess(MODULES.DASHBOARD)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'users.profile' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('profile'), 'text-muted': !hasModuleAccess(MODULES.PROFILE) }"
            >
                <i class="mdi mdi-account-circle-outline"></i> <span data-key="t-starter">Mon profil</span>
                <i v-if="!hasModuleAccess(MODULES.PROFILE)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="subscriptionStore.billingEnabled">
            <router-link
                :to="subscriptionRoute"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('subscription') }"
            >
                <i class="mdi mdi-credit-card-outline"></i> <span data-key="t-starter">{{ subscriptionLabel }}</span>
                <span v-if="!subscriptionStore.isSubscribed" class="badge badge-pill bg-warning">Pro</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="authUser.centers.length >= 1">
            <router-link
                :to="{ name: 'centers.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('centers'), 'text-muted': !hasModuleAccess(MODULES.CENTERS) }"
                v-if="authUser.centers.length > 1"
            >
                <i class="mdi mdi-office-building-marker-outline"></i> <span data-key="t-starter">Mes centres</span>
                <i v-if="!hasModuleAccess(MODULES.CENTERS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
            <router-link
                :to="{ name: 'centers.show', params: { id: authUser.centers[0].id } }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('centers'), 'text-muted': !hasModuleAccess(MODULES.CENTERS) }"
                v-else
            >
                <i class="mdi mdi-office-building-marker-outline"></i> <span data-key="t-starter">Mon centre</span>
                <i v-if="!hasModuleAccess(MODULES.CENTERS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'users.management-team' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('management-team') }">
                <i class="mdi mdi-account-star-outline"></i> <span data-key="t-starter">Team {{ brandConfig.companyName }}</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'chiptuning.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('chiptuning'), 'text-muted': !hasModuleAccess(MODULES.CHIPTUNING) }"
            >
                <i class="mdi mdi-speedometer"></i> <span data-key="t-starter">Reprogrammations</span>
                <i v-if="!hasModuleAccess(MODULES.CHIPTUNING)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="authUser.role === 'manager'">
            <router-link
                :to="{ name: 'statistics.index' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('statistics') }"
            >
                <i class="mdi mdi-chart-bar"></i> <span data-key="t-starter">Statistiques</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="brandConfig.shopUrl">
            <a :href="brandConfig.shopUrl" target="_blank" rel="noopener noreferrer" class="nav-link menu-link">
                <i class="mdi mdi-cart-outline"></i> <span data-key="t-starter">Shop</span>
                <i class="mdi mdi-open-in-new ms-auto"></i>
            </a>
        </li>

        <!-- GESTION CLIENTS -->
        <li class="menu-title"><span data-key="t-menu">Gestion clients</span></li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="authUser.role === 'manager'">
            <router-link
                :to="{ name: 'clients.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('clients'), 'text-muted': !hasModuleAccess(MODULES.CLIENTS) }"
            >
                <i class="mdi mdi-account-group-outline"></i> <span data-key="t-starter">Contacts</span>
                <i v-if="!hasModuleAccess(MODULES.CLIENTS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="authUser.role === 'manager'">
            <router-link
                :to="{ name: 'calls.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('calls'), 'text-muted': !hasModuleAccess(MODULES.CALLS) }"
            >
                <i class="mdi mdi-phone"></i> <span data-key="t-starter">Liste d'appels</span>
                <i v-if="!hasModuleAccess(MODULES.CALLS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'client-requests.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('client-requests') && isActiveMenuChild('client-requests.list'), 'text-muted': !hasModuleAccess(MODULES.CLIENT_REQUESTS) }"
            >
                <i class="mdi mdi-car-info"></i> <span data-key="t-starter">Demandes clients</span>
                <span class="badge badge-pill bg-danger" data-key="t-hot" v-if="notifications.notifications_client_requests && notifications.notifications_client_requests > 0 && hasModuleAccess(MODULES.CLIENT_REQUESTS)">{{ notifications.notifications_client_requests }}</span>
                <i v-if="!hasModuleAccess(MODULES.CLIENT_REQUESTS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'client-requests.agenda' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('client-requests') && isActiveMenuChild('client-requests.agenda'), 'text-muted': !hasModuleAccess(MODULES.CLIENT_REQUESTS) }"
            >
                <i class="mdi mdi-calendar"></i> <span data-key="t-starter">Agenda RDV</span>
                <i v-if="!hasModuleAccess(MODULES.CLIENT_REQUESTS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>


        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="subscriptionStore.billingEnabled">
            <router-link
                :to="{ name: 'quotes.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('quotes'), 'text-muted': !hasModuleAccess(MODULES.QUOTES) }"
            >
                <i class="mdi mdi-file-sign"></i> <span data-key="t-starter">Devis clients</span>
                <i v-if="!hasModuleAccess(MODULES.QUOTES)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="subscriptionStore.billingEnabled">
            <router-link
                :to="{ name: 'invoices.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('invoices'), 'text-muted': !hasModuleAccess(MODULES.INVOICES) }"
            >
                <i class="mdi mdi-bank"></i> <span data-key="t-starter">Factures clients</span>
                <i v-if="!hasModuleAccess(MODULES.INVOICES)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <!-- SUPPORT -->
        <li class="menu-title"><span data-key="t-menu">Support</span></li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'tickets.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('tickets'), 'text-muted': !hasModuleAccess(MODULES.TICKETS) }"
            >
                <i class="mdi mdi-tooltip-plus-outline"></i> <span data-key="t-starter">Tickets</span>
                <span class="badge badge-pill bg-danger" data-key="t-hot" v-if="notifications.notifications_tickets && notifications.notifications_tickets > 0 && hasModuleAccess(MODULES.TICKETS)">{{ notifications.notifications_tickets }}</span>
                <i v-if="!hasModuleAccess(MODULES.TICKETS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" >
            <router-link
                :to="{ name: 'help-articles.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('help-articles'), 'text-muted': !hasModuleAccess(MODULES.HELP_CENTER) }"
            >
                <i class="mdi mdi-help-circle-outline"></i> <span data-key="t-starter">Centre d'aide</span>
                <span class="badge badge-pill bg-info" v-if="notifications.help_article_available && hasModuleAccess(MODULES.HELP_CENTER)">New</span>
                <i v-if="!hasModuleAccess(MODULES.HELP_CENTER)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="authUser.role === 'manager'">
            <router-link
                :to="{ name: 'invoices-zoho.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('invoices-zoho'), 'text-muted': !hasModuleAccess(MODULES.INVOICES_ZOHO) }"
            >
                <i class="mdi mdi-sticker-text-outline"></i> <span data-key="t-starter">Factures Zoho</span>
                <i v-if="!hasModuleAccess(MODULES.INVOICES_ZOHO)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <!-- COMMUNICATION -->
        <li class="menu-title"><span data-key="t-menu">Communication</span></li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" >
            <router-link
                :to="{ name: 'news.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('news'), 'text-muted': !hasModuleAccess(MODULES.NEWS) }"
            >
                <i class="mdi mdi-newspaper"></i> <span data-key="t-starter">Actualités</span>
                <span class="badge badge-pill bg-info" v-if="notifications.news_available && hasModuleAccess(MODULES.NEWS)">New</span>
                <i v-if="!hasModuleAccess(MODULES.NEWS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'events.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('events'), 'text-muted': !hasModuleAccess(MODULES.EVENTS) }"
            >
                <i class="mdi mdi-calendar"></i> <span data-key="t-starter">Évènements</span>
                <i v-if="!hasModuleAccess(MODULES.EVENTS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'surveys.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('surveys'), 'text-muted': !hasModuleAccess(MODULES.SURVEYS) }"
            >
                <i class="mdi mdi-thumbs-up-down-outline"></i> <span data-key="t-starter">Formulaires</span>
                <i v-if="!hasModuleAccess(MODULES.SURVEYS)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link
                :to="{ name: 'social-network-media.list' }"
                class="nav-link menu-link"
                :class="{ 'active': isActiveMenuParent('social-network-media'), 'text-muted': !hasModuleAccess(MODULES.MEDIA_LIBRARY) }"
            >
                <i class="mdi mdi-instagram"></i> <span data-key="t-starter">Biblio. médias</span>
                <span class="badge badge-pill bg-success" v-if="notifications.social_network_media_available && hasModuleAccess(MODULES.MEDIA_LIBRARY)">New</span>
                <i v-if="!hasModuleAccess(MODULES.MEDIA_LIBRARY)" class="mdi mdi-lock-outline ms-auto"></i>
            </router-link>
        </li>
    </ul>
</template>

<style scoped>

</style>
